import React, { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import { useAuthController } from "@camberi/firecms";

type VersionMenuProps = {
	asset: any;
	version: any;
	onClick: any;
};

function getVersionId(versionEntry: any) {
	return versionEntry?.ref?.id || versionEntry?.id || "";
}

function normalizeText(value: any) {
	return String(value || "").trim();
}

function getFirstText(...values: any[]) {
	for (const value of values) {
		const text = normalizeText(value);

		if (text.length) {
			return text;
		}
	}

	return "";
}

function getVersionComment(versionEntry: any) {
	return getFirstText(
		versionEntry?.commit_message,
		versionEntry?.commitMessage,
		versionEntry?.comment,
		versionEntry?.comments,
		versionEntry?.description,
		versionEntry?.note,
		versionEntry?.notes,
		versionEntry?.values?.commit_message,
		versionEntry?.values?.commitMessage,
		versionEntry?.values?.comment,
		versionEntry?.values?.comments,
		versionEntry?.values?.description,
		versionEntry?.values?.note,
		versionEntry?.values?.notes
	);
}

function formatVersionNameCandidate(value: any) {
	const text = normalizeText(value);

	if (!text.length) {
		return "";
	}

	if (/^v\d+$/i.test(text)) {
		return text.toLowerCase();
	}

	if (/^\d+$/.test(text)) {
		return `v${text}`;
	}

	return text;
}

function getVersionName(versionEntry: any, fallbackIndexFromNewest: number) {
	const directName = getFirstText(
		versionEntry?.name,
		versionEntry?.label,
		versionEntry?.version_name,
		versionEntry?.versionName,
		versionEntry?.display_name,
		versionEntry?.displayName,
		versionEntry?.values?.name,
		versionEntry?.values?.label,
		versionEntry?.values?.version_name,
		versionEntry?.values?.versionName,
		versionEntry?.values?.display_name,
		versionEntry?.values?.displayName
	);

	const formattedDirectName = formatVersionNameCandidate(directName);

	if (formattedDirectName.length) {
		return formattedDirectName;
	}

	const versionNumber = getFirstText(
		versionEntry?.asset_version_number,
		versionEntry?.version,
		versionEntry?.version_number,
		versionEntry?.versionNumber,
		versionEntry?.number,
		versionEntry?.values?.asset_version_number,
		versionEntry?.values?.version,
		versionEntry?.values?.version_number,
		versionEntry?.values?.versionNumber,
		versionEntry?.values?.number
	);

	const formattedVersionNumber = formatVersionNameCandidate(versionNumber);

	if (formattedVersionNumber.length) {
		return formattedVersionNumber;
	}

	const fallbackVersionNumber = fallbackIndexFromNewest + 1;

	return `v${fallbackVersionNumber}`;
}

function getSortedVersions(asset: any) {
	return [...(asset?.values?.versions || [])].filter((versionEntry: any) => Boolean(getVersionId(versionEntry))).sort((a: any, b: any) => getVersionId(b).localeCompare(getVersionId(a)));
}

export default function VersionMenu({ onClick, asset, version }: VersionMenuProps) {
	const authController = useAuthController();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [curAsset, setCurAsset] = useState<any>({});
	const [curVersion, setCurVersion] = useState<any>({});

	useEffect(() => {
		setCurAsset(asset);
	}, [asset]);

	useEffect(() => {
		setCurVersion(version);
	}, [version]);

	const versions = useMemo(() => {
		return getSortedVersions(asset);
	}, [asset]);

	const open = Boolean(anchorEl);

	const currentVersionId = curVersion?.id || version?.id || "";

	const selectedVersionIndex = useMemo(() => {
		return versions.findIndex((versionEntry: any) => getVersionId(versionEntry) === currentVersionId);
	}, [currentVersionId, versions]);

	const selectedVersionEntry = selectedVersionIndex > -1 ? versions[selectedVersionIndex] : null;

	const selectedVersionLabel = selectedVersionEntry ? getVersionName(selectedVersionEntry, selectedVersionIndex) : formatVersionNameCandidate(curVersion?.values?.asset_version_number) || "";

	const selectedVersionComment = selectedVersionEntry ? getVersionComment(selectedVersionEntry) : "";

	const defaultPrevent = (event: any) => {
		event?.preventDefault?.();
		event?.stopPropagation?.();
	};

	const handleClose = (event?: any) => {
		defaultPrevent(event);
		setAnchorEl(null);
	};

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		defaultPrevent(event);

		onClick?.();
		setAnchorEl(event.currentTarget);
	};

	const handleSelectVersion = (event: any, nextVersionId: string, isSelectedVersion: boolean) => {
		defaultPrevent(event);

		if (!isSelectedVersion) {
			authController?.extra?.playerDialogHandleOpen(asset?.id, nextVersionId, "");
		}

		setAnchorEl(null);
	};

	const VersionItem = ({ version: versionEntry, index }: { version: any; index: number }) => {
		const versionId = getVersionId(versionEntry);
		const isSelectedVersion = Boolean(currentVersionId?.length && currentVersionId === versionId);
		const comment = getVersionComment(versionEntry);
		const versionName = getVersionName(versionEntry, index);

		return (
			<MenuItem
				key={`${versionId}_selector`}
				dense
				selected={isSelectedVersion}
				autoFocus={isSelectedVersion}
				onClick={(evt) => handleSelectVersion(evt, versionId, isSelectedVersion)}
				sx={{
					cursor: isSelectedVersion ? "default" : "pointer",
					"&.Mui-selected": {
						backgroundColor: "rgba(16, 185, 129, 0.16)",
					},
					"&.Mui-selected:hover": {
						backgroundColor: "rgba(16, 185, 129, 0.22)",
					},
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						minWidth: 0,
						width: "100%",
					}}
				>
					<Box
						sx={{
							width: 8,
							height: 8,
							borderRadius: "50%",
							mr: 1,
							flex: "0 0 auto",
							backgroundColor: isSelectedVersion ? "primary.main" : "transparent",
						}}
					/>

					<ListItemText
						primary={
							<Box
								component="span"
								sx={{
									display: "flex",
									alignItems: "baseline",
									gap: 0.75,
									minWidth: 0,
								}}
							>
								<Box
									component="span"
									sx={{
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										fontWeight: 700,
									}}
								>
									{versionName}
								</Box>

								{isSelectedVersion ? (
									<Box
										component="span"
										sx={{
											flex: "0 0 auto",
											opacity: 0.75,
											fontSize: "0.75rem",
											fontWeight: 700,
										}}
									>
										(current)
									</Box>
								) : null}
							</Box>
						}
						secondary={comment || undefined}
						primaryTypographyProps={{
							noWrap: true,
							component: "div",
							fontWeight: 700,
							fontSize: "0.875rem",
							lineHeight: 1.75,
						}}
						secondaryTypographyProps={{
							noWrap: true,
							title: comment,
							fontSize: "0.75rem",
							lineHeight: 1.4,
							color: "text.secondary",
						}}
						sx={{
							my: 0,
							minWidth: 0,
							maxWidth: 300,
						}}
					/>
				</Box>
			</MenuItem>
		);
	};

	return (
		<div>
			<Button
				color="info"
				size="small"
				variant="text"
				disabled={!versions.length}
				onClick={handleClick}
				endIcon={<ArrowDropDownIcon fontSize="inherit" />}
				disableRipple
				disableElevation
				sx={{
					zIndex: 50000,
					ml: 0.5,
					height: "32px",
					minHeight: "32px",
					maxWidth: 220,
					px: 1,
					borderRadius: 1,
					color: "white",
					backgroundColor: "rgba(255,255,255,0.08)",
					borderColor: "rgba(255,255,255,0.05)",
					textTransform: "none",
					fontWeight: 700,
					fontSize: "0.875rem",
					lineHeight: 1.75,
					whiteSpace: "nowrap",
					overflow: "hidden",
					minWidth: "inherit",
					"&:hover": {
						backgroundColor: "rgba(255,255,255,0.12)",
					},
					"& .MuiButton-endIcon": {
						ml: 0.25,
					},
				}}
			>
				<Box
					component="span"
					sx={{
						display: "block",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						fontWeight: 700,
					}}
				>
					{selectedVersionLabel}
				</Box>
			</Button>

			<Menu
				id="version-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				onClick={defaultPrevent}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				MenuListProps={{
					"aria-labelledby": "version-menu-button",
				}}
				PaperProps={{
					style: {
						maxHeight: "33%",
						left: "50%",
						transform: "translateY(10px)",
					},
				}}
			>
				{/* <MenuItem disabled dense>
					<ListItemText
						primary="Version"
						secondary={selectedVersionLabel ? (selectedVersionComment ? `Current: ${selectedVersionLabel} · ${selectedVersionComment}` : `Current: ${selectedVersionLabel}`) : "Select a version"}
						primaryTypographyProps={{
							noWrap: true,
							fontWeight: 700,
							fontSize: "0.875rem",
							lineHeight: 1.75,
						}}
						secondaryTypographyProps={{
							noWrap: true,
							fontSize: "0.75rem",
							lineHeight: 1.4,
							color: "text.secondary",
						}}
						sx={{
							my: 0,
							minWidth: 0,
							maxWidth: 300,
						}}
					/>
				</MenuItem> */}

				{versions.map((versionEntry: any, index: number) => {
					return <VersionItem key={`${getVersionId(versionEntry)}_selector`} version={versionEntry} index={index} />;
				})}
			</Menu>
		</div>
	);
}
